import { useState, useEffect } from "react";
import { useAuth, apiClient } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  BarChart3, 
  TrendingUp, 
  DollarSign,
  Users,
  Target,
  Building2,
  Calendar,
  Download,
  RefreshCw,
  Stethoscope,
  Activity,
  UserCheck,
  Percent,
  Layers,
  ChevronDown,
  ChevronUp,
  MapPin,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  RadialBarChart,
  RadialBar
} from "recharts";

const COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

export default function ReportsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("mtd");
  const [activeTab, setActiveTab] = useState("pnl");
  
  const [pnlData, setPnlData] = useState(null);
  const [audiologistData, setAudiologistData] = useState(null);
  const [leadSourceData, setLeadSourceData] = useState(null);
  const [clusterData, setClusterData] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [expandedCluster, setExpandedCluster] = useState(null);

  useEffect(() => {
    fetchAllReports();
  }, [period]);

  const fetchAllReports = async () => {
    setLoading(true);
    try {
      const [pnl, audio, leadSource, cluster, dashboard] = await Promise.all([
        apiClient.get(`/reports/pnl?period=${period}`),
        apiClient.get(`/reports/audiologist?period=${period}`),
        apiClient.get(`/reports/lead-source?period=${period}`),
        apiClient.get(`/reports/cluster?period=${period}`).catch(() => ({ data: { cluster_performance: [], summary: {} } })),
        apiClient.get("/reports/dashboard-summary")
      ]);
      
      setPnlData(pnl.data);
      setAudiologistData(audio.data);
      setLeadSourceData(leadSource.data);
      setClusterData(cluster.data);
      setDashboardData(dashboard.data);
    } catch (error) {
      console.error("Failed to fetch reports:", error);
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return "₹0";
    if (value >= 100000) {
      return `₹${(value / 100000).toFixed(1)}L`;
    }
    return `₹${value?.toLocaleString() || 0}`;
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="reports-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-indigo-600" />
              Reports & Analytics
            </h1>
            <p className="text-slate-500">Business performance insights and metrics</p>
          </div>

          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40" data-testid="period-selector">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Today</SelectItem>
                <SelectItem value="mtd">Month to Date</SelectItem>
                <SelectItem value="ytd">Year to Date</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchAllReports} disabled={loading} data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        {dashboardData && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-emerald-600">Today's Revenue</p>
                    <p className="text-xl font-bold text-emerald-700">{formatCurrency(dashboardData.today?.revenue)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-blue-600">MTD Revenue</p>
                    <p className="text-xl font-bold text-blue-700">{formatCurrency(dashboardData.mtd?.revenue)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-50 to-amber-100">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                    <Activity className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-amber-600">Active Trials</p>
                    <p className="text-xl font-bold text-amber-700">{dashboardData.active_trials}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-purple-600">New Patients</p>
                    <p className="text-xl font-bold text-purple-700">{dashboardData.today?.new_patients}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-cyan-500 rounded-lg flex items-center justify-center">
                    <Stethoscope className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-cyan-600">Tests Today</p>
                    <p className="text-xl font-bold text-cyan-700">{dashboardData.today?.tests_conducted}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Report Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 w-full max-w-2xl">
            <TabsTrigger value="pnl" data-testid="tab-pnl">P&L</TabsTrigger>
            <TabsTrigger value="audiologist" data-testid="tab-audiologist">Audiologist</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads">Lead Source</TabsTrigger>
            <TabsTrigger value="cluster" data-testid="tab-cluster">Cluster</TabsTrigger>
          </TabsList>

          {/* P&L Report */}
          <TabsContent value="pnl" className="mt-6" data-testid="pnl-content">
            {pnlData && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Revenue Summary - {pnlData.period_label}</CardTitle>
                    <CardDescription>
                      {pnlData.date_range?.start?.slice(0, 10)} to {pnlData.date_range?.end?.slice(0, 10)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                      <div className="p-4 bg-emerald-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-emerald-700">{formatCurrency(pnlData.summary?.total_revenue)}</p>
                        <p className="text-sm text-emerald-600">Total Revenue</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-green-700">{formatCurrency(pnlData.summary?.paid_revenue)}</p>
                        <p className="text-sm text-green-600">Paid</p>
                      </div>
                      <div className="p-4 bg-amber-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-amber-700">{formatCurrency(pnlData.summary?.pending_revenue)}</p>
                        <p className="text-sm text-amber-600">Pending</p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-blue-700">{pnlData.summary?.total_units}</p>
                        <p className="text-sm text-blue-600">Units Sold</p>
                      </div>
                    </div>

                    {/* Clinic Breakdown Chart */}
                    {pnlData.clinic_breakdown?.length > 0 && (
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={pnlData.clinic_breakdown.slice(0, 10)} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} />
                            <YAxis dataKey="clinic_name" type="category" width={150} tick={{ fontSize: 12 }} />
                            <Tooltip formatter={(v) => formatCurrency(v)} />
                            <Bar dataKey="revenue" fill="#10b981" name="Revenue" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Clinic Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Clinic-wise Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4">Clinic</th>
                            <th className="text-left py-3 px-4">City</th>
                            <th className="text-right py-3 px-4">Revenue</th>
                            <th className="text-right py-3 px-4">Units</th>
                            <th className="text-right py-3 px-4">Avg Order</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pnlData.clinic_breakdown?.map((clinic, idx) => (
                            <tr key={clinic.clinic_id} className="border-b hover:bg-slate-50">
                              <td className="py-3 px-4 font-medium">{clinic.clinic_name}</td>
                              <td className="py-3 px-4 text-slate-500">{clinic.city}</td>
                              <td className="py-3 px-4 text-right text-emerald-600 font-semibold">
                                {formatCurrency(clinic.revenue)}
                              </td>
                              <td className="py-3 px-4 text-right">{clinic.units}</td>
                              <td className="py-3 px-4 text-right">{formatCurrency(clinic.avg_order_value)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Audiologist Report - ENHANCED */}
          <TabsContent value="audiologist" className="mt-6" data-testid="audiologist-content">
            {audiologistData && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                          <Users className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-blue-600">Audiologists</p>
                          <p className="text-xl font-bold text-blue-700">{audiologistData.summary?.total_audiologists || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100/50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
                          <Stethoscope className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-indigo-600">Total Tests</p>
                          <p className="text-xl font-bold text-indigo-700">{audiologistData.summary?.total_tests || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                          <DollarSign className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-emerald-600">Total Revenue</p>
                          <p className="text-xl font-bold text-emerald-700">{formatCurrency(audiologistData.summary?.total_revenue)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                          <UserCheck className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-amber-600">Conversions</p>
                          <p className="text-xl font-bold text-amber-700">{audiologistData.summary?.total_conversions || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                          <Target className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-purple-600">Avg Tests/Audiologist</p>
                          <p className="text-xl font-bold text-purple-700">{audiologistData.summary?.avg_tests_per_audiologist || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Performance Chart */}
                {audiologistData.audiologist_performance?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Audiologist Performance Comparison</CardTitle>
                      <CardDescription>{audiologistData.period_label}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={audiologistData.audiologist_performance.slice(0, 10)} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis dataKey="audiologist_name" type="category" width={120} tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="metrics.pta_tests" name="PTA Tests" fill="#0ea5e9" stackId="tests" />
                            <Bar dataKey="metrics.speech_tests" name="Speech Tests" fill="#8b5cf6" stackId="tests" />
                            <Bar dataKey="metrics.tympanometry_tests" name="Tympanometry" fill="#f59e0b" stackId="tests" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Detailed Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Audiologist Performance Details</CardTitle>
                    <CardDescription>Tests, recommendations, and conversion metrics</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left py-3 px-4">Audiologist</th>
                            <th className="text-left py-3 px-4">Clinic</th>
                            <th className="text-center py-3 px-4">Total Tests</th>
                            <th className="text-center py-3 px-4">PTA</th>
                            <th className="text-center py-3 px-4">Speech</th>
                            <th className="text-center py-3 px-4">Tymp</th>
                            <th className="text-center py-3 px-4">Recs</th>
                            <th className="text-center py-3 px-4">Conversions</th>
                            <th className="text-center py-3 px-4">Conv %</th>
                            <th className="text-right py-3 px-4">Revenue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {audiologistData.audiologist_performance?.map((audio) => (
                            <tr key={audio.audiologist_id} className="border-b hover:bg-slate-50">
                              <td className="py-3 px-4 font-medium">{audio.audiologist_name}</td>
                              <td className="py-3 px-4 text-slate-500 text-xs">
                                {audio.clinic_name}
                                {audio.clinic_city && <span className="block text-slate-400">{audio.clinic_city}</span>}
                              </td>
                              <td className="py-3 px-4 text-center">
                                <Badge className="bg-blue-100 text-blue-700">{audio.metrics.total_tests}</Badge>
                              </td>
                              <td className="py-3 px-4 text-center">{audio.metrics.pta_tests}</td>
                              <td className="py-3 px-4 text-center">{audio.metrics.speech_tests}</td>
                              <td className="py-3 px-4 text-center">{audio.metrics.tympanometry_tests}</td>
                              <td className="py-3 px-4 text-center">{audio.metrics.recommendations}</td>
                              <td className="py-3 px-4 text-center">
                                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                  {audio.metrics.conversions}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <span className={audio.metrics.conversion_rate >= 30 ? "text-emerald-600" : audio.metrics.conversion_rate >= 15 ? "text-amber-600" : "text-slate-500"}>
                                    {audio.metrics.conversion_rate}%
                                  </span>
                                  {audio.metrics.conversion_rate >= 30 ? (
                                    <ArrowUpRight className="w-3 h-3 text-emerald-500" />
                                  ) : audio.metrics.conversion_rate < 15 ? (
                                    <ArrowDownRight className="w-3 h-3 text-red-400" />
                                  ) : null}
                                </div>
                              </td>
                              <td className="py-3 px-4 text-right text-emerald-600 font-semibold">
                                {formatCurrency(audio.metrics.revenue_attributed)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Lead Source Report - ENHANCED */}
          <TabsContent value="leads" className="mt-6" data-testid="leads-content">
            {leadSourceData && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                          <Users className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-blue-600">Total Leads</p>
                          <p className="text-xl font-bold text-blue-700">{leadSourceData.summary?.total_leads || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                          <UserCheck className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-emerald-600">Converted</p>
                          <p className="text-xl font-bold text-emerald-700">{leadSourceData.summary?.total_converted || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                          <Percent className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-amber-600">Conversion Rate</p>
                          <p className="text-xl font-bold text-amber-700">{leadSourceData.summary?.overall_conversion_rate || 0}%</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                          <DollarSign className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-purple-600">Total Revenue</p>
                          <p className="text-xl font-bold text-purple-700">{formatCurrency(leadSourceData.summary?.total_revenue)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Pie Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Lead Distribution by Source</CardTitle>
                      <CardDescription>{leadSourceData.period_label}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={leadSourceData.lead_source_performance}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ source, percent }) => `${source.replace(/_/g, ' ')} ${(percent * 100).toFixed(0)}%`}
                              outerRadius={100}
                              fill="#8884d8"
                              dataKey="total_leads"
                            >
                              {leadSourceData.lead_source_performance?.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Conversion Comparison Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Conversion by Source</CardTitle>
                      <CardDescription>Converted vs Lost leads by source</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={leadSourceData.lead_source_performance} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis dataKey="source" type="category" width={80} tick={{ fontSize: 11 }} tickFormatter={(v) => v.replace(/_/g, ' ')} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="converted" name="Converted" fill="#10b981" />
                            <Bar dataKey="active" name="Active" fill="#f59e0b" />
                            <Bar dataKey="lost" name="Lost" fill="#ef4444" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Detailed Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Lead Source Performance Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left py-3 px-4">Source</th>
                            <th className="text-center py-3 px-4">Total Leads</th>
                            <th className="text-center py-3 px-4">Converted</th>
                            <th className="text-center py-3 px-4">Active</th>
                            <th className="text-center py-3 px-4">Lost</th>
                            <th className="text-center py-3 px-4">Conv %</th>
                            <th className="text-center py-3 px-4">Loss %</th>
                            <th className="text-right py-3 px-4">Revenue</th>
                            <th className="text-right py-3 px-4">Avg Deal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {leadSourceData.lead_source_performance?.map((source, idx) => (
                            <tr key={source.source} className="border-b hover:bg-slate-50">
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-3 h-3 rounded-full" 
                                    style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                                  />
                                  <span className="font-medium capitalize">{source.source.replace(/_/g, ' ')}</span>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <Badge variant="outline">{source.total_leads}</Badge>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <Badge className="bg-emerald-100 text-emerald-700">{source.converted}</Badge>
                              </td>
                              <td className="py-3 px-4 text-center text-amber-600">{source.active}</td>
                              <td className="py-3 px-4 text-center text-red-500">{source.lost}</td>
                              <td className="py-3 px-4 text-center">
                                <span className={source.conversion_rate >= 25 ? "text-emerald-600 font-semibold" : source.conversion_rate >= 10 ? "text-amber-600" : "text-slate-500"}>
                                  {source.conversion_rate}%
                                </span>
                              </td>
                              <td className="py-3 px-4 text-center text-red-500">{source.loss_rate}%</td>
                              <td className="py-3 px-4 text-right text-emerald-600 font-semibold">
                                {formatCurrency(source.revenue)}
                              </td>
                              <td className="py-3 px-4 text-right text-slate-600">
                                {formatCurrency(source.avg_deal_value)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Cluster Report - ENHANCED */}
          <TabsContent value="cluster" className="mt-6" data-testid="cluster-content">
            {clusterData && clusterData.cluster_performance?.length > 0 ? (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100/50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
                          <Layers className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-indigo-600">Total Clusters</p>
                          <p className="text-xl font-bold text-indigo-700">{clusterData.summary?.total_clusters || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                          <DollarSign className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-emerald-600">Total Revenue</p>
                          <p className="text-xl font-bold text-emerald-700">{formatCurrency(clusterData.summary?.total_revenue)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                          <Users className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-blue-600">Total Leads</p>
                          <p className="text-xl font-bold text-blue-700">{clusterData.summary?.total_leads || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                          <Percent className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-amber-600">Conversion Rate</p>
                          <p className="text-xl font-bold text-amber-700">{clusterData.summary?.overall_conversion_rate || 0}%</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Cluster Revenue Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle>Cluster Revenue Comparison</CardTitle>
                    <CardDescription>{clusterData.period_label}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={clusterData.cluster_performance}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="cluster_name" tick={{ fontSize: 11 }} />
                          <YAxis tickFormatter={(v) => formatCurrency(v)} />
                          <Tooltip formatter={(v, name) => name === "Total Revenue" ? formatCurrency(v) : v} />
                          <Legend />
                          <Bar dataKey="metrics.total_revenue" name="Total Revenue" fill="#10b981" />
                          <Bar dataKey="metrics.paid_revenue" name="Paid Revenue" fill="#0ea5e9" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Cluster Cards with Expandable Clinic Breakdown */}
                <div className="space-y-4">
                  {clusterData.cluster_performance.map((cluster) => (
                    <Card key={cluster.cluster_id} className="overflow-hidden">
                      <div 
                        className="p-5 cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => setExpandedCluster(expandedCluster === cluster.cluster_id ? null : cluster.cluster_id)}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-lg text-slate-900">{cluster.cluster_name}</h3>
                              <p className="text-sm text-slate-500">
                                Head: {cluster.head_name || "Not assigned"} • {cluster.clinic_count} clinics
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className="bg-emerald-100 text-emerald-700 text-base px-3 py-1">
                              {formatCurrency(cluster.metrics.total_revenue)}
                            </Badge>
                            {expandedCluster === cluster.cluster_id ? (
                              <ChevronUp className="w-5 h-5 text-slate-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-slate-400" />
                            )}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                          <div className="text-center p-3 bg-slate-50 rounded-lg">
                            <p className="text-xl font-bold text-slate-900">{cluster.metrics.total_units}</p>
                            <p className="text-xs text-slate-500">Units Sold</p>
                          </div>
                          <div className="text-center p-3 bg-emerald-50 rounded-lg">
                            <p className="text-xl font-bold text-emerald-700">{formatCurrency(cluster.metrics.paid_revenue)}</p>
                            <p className="text-xs text-emerald-600">Paid</p>
                          </div>
                          <div className="text-center p-3 bg-amber-50 rounded-lg">
                            <p className="text-xl font-bold text-amber-700">{formatCurrency(cluster.metrics.pending_revenue)}</p>
                            <p className="text-xs text-amber-600">Pending</p>
                          </div>
                          <div className="text-center p-3 bg-blue-50 rounded-lg">
                            <p className="text-xl font-bold text-blue-700">{cluster.metrics.total_leads}</p>
                            <p className="text-xs text-blue-600">Leads</p>
                          </div>
                          <div className="text-center p-3 bg-purple-50 rounded-lg">
                            <p className="text-xl font-bold text-purple-700">{cluster.metrics.converted_leads}</p>
                            <p className="text-xs text-purple-600">Converted</p>
                          </div>
                          <div className="text-center p-3 bg-indigo-50 rounded-lg">
                            <p className="text-xl font-bold text-indigo-700">{cluster.metrics.conversion_rate}%</p>
                            <p className="text-xs text-indigo-600">Conv Rate</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Expandable Clinic Breakdown */}
                      {expandedCluster === cluster.cluster_id && cluster.clinic_breakdown?.length > 0 && (
                        <div className="border-t bg-slate-50/50 p-5">
                          <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            Clinic Breakdown
                          </h4>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b">
                                  <th className="text-left py-2 px-3">Clinic</th>
                                  <th className="text-left py-2 px-3">City</th>
                                  <th className="text-right py-2 px-3">Revenue</th>
                                  <th className="text-right py-2 px-3">Units</th>
                                  <th className="text-right py-2 px-3">Leads</th>
                                  <th className="text-right py-2 px-3">Converted</th>
                                  <th className="text-right py-2 px-3">Conv %</th>
                                </tr>
                              </thead>
                              <tbody>
                                {cluster.clinic_breakdown.map((clinic) => (
                                  <tr key={clinic.clinic_id} className="border-b hover:bg-white">
                                    <td className="py-2 px-3 font-medium">{clinic.clinic_name}</td>
                                    <td className="py-2 px-3 text-slate-500">{clinic.city}</td>
                                    <td className="py-2 px-3 text-right text-emerald-600 font-semibold">
                                      {formatCurrency(clinic.revenue)}
                                    </td>
                                    <td className="py-2 px-3 text-right">{clinic.units}</td>
                                    <td className="py-2 px-3 text-right">{clinic.leads}</td>
                                    <td className="py-2 px-3 text-right">{clinic.converted}</td>
                                    <td className="py-2 px-3 text-right">
                                      <span className={clinic.conversion_rate >= 25 ? "text-emerald-600" : "text-slate-500"}>
                                        {clinic.conversion_rate}%
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            ) : (
              <Card>
                <CardContent className="text-center py-12">
                  <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No clusters defined yet</p>
                  <p className="text-sm text-slate-400 mt-1">Create clusters in the admin section to see cluster-wise reports</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
